import React, { useState, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import GeneralTab from "./tabs/GeneralTab";
import IntegrationsTab from "./tabs/IntegrationsTab";
import UsageTab from "./tabs/UsageTab";
import { trackEvent } from "../../utils/events";
import CustomTab from "./tabs/CustomTab";
import SkillsTab from "./tabs/SkillsTab";
import BillingTab from "./tabs/BillingTab";
import MCPAdminTab from "./tabs/MCPAdminTab";
import TeamTab from "./tabs/TeamTab";

import { useFeatureFlag, FLAGS } from "../../config/featureFlags";
import { HelpDot } from "./AgenticGlossary";
import "./Settings.css";

/**
 * Settings - Main container with tab navigation
 *
 * Modular architecture:
 * - Each tab is a separate component
 * - Easy to add new tabs
 * - Clean separation of concerns
 * - Apple-level polish
 */
/**
 * @param {string|null} initialTab - open straight onto a tab; "integrations#custom-tools" also scrolls to that section
 * @param {(() => void)|null} onOpenDashboards - the Custom tab's Dashboards card (null when dashboards are off)
 */
const Settings = ({ onClose, onShowTutorial, initialTab = null, onOpenDashboards = null }) => {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState((initialTab || "integrations").split("#")[0]);
  const [anchor, setAnchor] = useState((initialTab || "").split("#")[1] || null);
  const goToTab = (tab, section = null) => { setActiveTab(tab); setAnchor(section); trackEvent("tab_opened", { tab, via: "custom" }); };
  useEffect(() => {
    if (!anchor) return undefined;
    const t = setTimeout(() => { try { document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (_) { /* noop */ } }, 250);
    return () => clearTimeout(t);
  }, [anchor, activeTab]);

  // Check if user is admin for MCP Admin tab
  // Requires BOTH: feature flag enabled AND Clerk admin role
  const mcpAdminEnabled = useFeatureFlag(FLAGS.MCP_ADMIN);
  const customToolsEnabled = useFeatureFlag("custom_tools");
  const isAdmin = user?.publicMetadata?.role === "admin" || process.env.NODE_ENV !== "production";

  const showMcpAdmin = mcpAdminEnabled && isAdmin;

  const tabs = [
    { id: "general", label: "General", icon: "⚙️" },
    { id: "integrations", label: "Integrations", icon: "🔌" },
    { id: "skills", label: "Skills", icon: "🧰" },
    { id: "custom", label: "Custom", icon: "🛠️" },
    { id: "team", label: "Team", icon: "👥" },
    { id: "usage", label: "Usage", icon: "📊" },
    { id: "billing", label: "Billing", icon: "💳" },
    // MCP Admin tab (requires feature flag + admin role)
    ...(showMcpAdmin
      ? [{ id: "mcp-admin", label: "MCP Admin", icon: "🚀" }]
      : []),
  ];

  const renderTab = () => {
    switch (activeTab) {
      case "general":
        return <GeneralTab onShowTutorial={onShowTutorial} />;
      case "integrations":
        return <IntegrationsTab />;
      case "skills":
        return <SkillsTab />;
      case "custom":
        return <CustomTab onOpenDashboards={onOpenDashboards} onGoToTab={goToTab} dashboardsEnabled={!!onOpenDashboards} customToolsEnabled={customToolsEnabled} />;
      case "team":
        return <TeamTab />;
      case "usage":
        return <UsageTab />;
      case "billing":
        return <BillingTab />;
      case "mcp-admin":
        return <MCPAdminTab />;
      default:
        return <IntegrationsTab />;
    }
  };

  return (
    <div className="settings-overlay">
      <div className="settings-container hf-reveal-scale">
        {/* Header */}
        <div className="settings-header">
          <div>
            <h2>
              Settings
              <HelpDot label="What do these words mean? Open the agentic glossary" />
            </h2>
            <p>Manage your account, integrations, and preferences</p>
          </div>
          <button onClick={onClose} className="close-button">
            ✕
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="settings-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); trackEvent("tab_opened", { tab: tab.id }); }}
              className={`tab ${activeTab === tab.id ? "active" : ""}`}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="settings-content">{renderTab()}</div>
      </div>
    </div>
  );
};

export default Settings;
